import type { ApiResponse, ApiError, API_ENDPOINTS } from '@/types/api';
import type { AuthTokens } from '@/types/auth';

type Endpoint = (typeof API_ENDPOINTS)[keyof typeof API_ENDPOINTS];

/** Token storage keys */
const ACCESS_TOKEN_KEY = 'hks_access_token';
const REFRESH_TOKEN_KEY = 'hks_refresh_token';

/** Event emitter for auth state changes */
type AuthEventType = 'token_refreshed' | 'token_expired' | 'logout';
type AuthEventHandler = (data?: unknown) => void;

class ApiClient {
  private baseUrl: string;
  private listeners = new Map<AuthEventType, Set<AuthEventHandler>>();
  private refreshPromise: Promise<AuthTokens> | null = null;

  constructor(baseUrl: string = '') {
    this.baseUrl = baseUrl;
  }

  /** Subscribe to auth events */
  on(event: AuthEventType, handler: AuthEventHandler): () => void {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set());
    }
    this.listeners.get(event)!.add(handler);
    return () => this.listeners.get(event)?.delete(handler);
  }

  /** Emit auth event */
  private emit(event: AuthEventType, data?: unknown): void {
    this.listeners.get(event)?.forEach((handler) => handler(data));
  }

  /** Get stored access token */
  getAccessToken(): string | null {
    return localStorage.getItem(ACCESS_TOKEN_KEY);
  }

  /** Get stored refresh token */
  getRefreshToken(): string | null {
    return localStorage.getItem(REFRESH_TOKEN_KEY);
  }

  /** Store tokens */
  setTokens(tokens: AuthTokens): void {
    localStorage.setItem(ACCESS_TOKEN_KEY, tokens.accessToken);
    localStorage.setItem(REFRESH_TOKEN_KEY, tokens.refreshToken);
  }

  /** Clear tokens */
  clearTokens(): void {
    localStorage.removeItem(ACCESS_TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
  }

  /** Check if user has tokens */
  hasTokens(): boolean {
    return !!this.getAccessToken();
  }

  /** Build headers with auth */
  private buildHeaders(extra: Record<string, string> = {}): Headers {
    const headers = new Headers({
      'Content-Type': 'application/json',
      ...extra,
    });
    const token = this.getAccessToken();
    if (token) {
      headers.set('Authorization', `Bearer ${token}`);
    }
    return headers;
  }

  /** Core fetch wrapper with auth handling */
  async request<T>(
    endpoint: string,
    options: {
      method?: string;
      body?: unknown;
      headers?: Record<string, string>;
      skipAuth?: boolean;
    } = {},
  ): Promise<ApiResponse<T>> {
    const { method = 'GET', body, headers: extraHeaders = {}, skipAuth = false } = options;
    const url = `${this.baseUrl}${endpoint}`;

    const headers = skipAuth
      ? new Headers({ 'Content-Type': 'application/json', ...extraHeaders })
      : this.buildHeaders(extraHeaders);

    const fetchOptions: RequestInit = {
      method,
      headers,
    };

    if (body && method !== 'GET') {
      fetchOptions.body = JSON.stringify(body);
    }

    let response = await fetch(url, fetchOptions);

    // Handle 401 — try to refresh token
    if (response.status === 401 && !skipAuth) {
      try {
        await this.refreshAccessToken();
        // Retry with new token
        const retryHeaders = this.buildHeaders(extraHeaders);
        response = await fetch(url, { ...fetchOptions, headers: retryHeaders });
      } catch {
        this.clearTokens();
        this.emit('token_expired');
        throw new ApiClientError('Session expired', 'SESSION_EXPIRED', 401);
      }
    }

    const data = await response.json();

    if (!response.ok) {
      const errorData = data as ApiError;
      throw new ApiClientError(
        errorData.error?.message ?? 'Request failed',
        errorData.error?.code ?? 'UNKNOWN',
        response.status,
        errorData.error?.details,
      );
    }

    return data as ApiResponse<T>;
  }

  /** GET request */
  async get<T>(endpoint: string, params?: Record<string, string>): Promise<ApiResponse<T>> {
    let url = endpoint;
    if (params) {
      const searchParams = new URLSearchParams(params);
      url += `?${searchParams.toString()}`;
    }
    return this.request<T>(url, { method: 'GET' });
  }

  /** POST request */
  async post<T>(endpoint: string, body?: unknown): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, { method: 'POST', body });
  }

  /** PUT request */
  async put<T>(endpoint: string, body?: unknown): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, { method: 'PUT', body });
  }

  /** DELETE request */
  async delete<T>(endpoint: string): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, { method: 'DELETE' });
  }

  /** PATCH request */
  async patch<T>(endpoint: string, body?: unknown): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, { method: 'PATCH', body });
  }

  /** Refresh the access token using the refresh token */
  async refreshAccessToken(): Promise<AuthTokens> {
    // Deduplicate concurrent refresh requests
    if (this.refreshPromise) return this.refreshPromise;

    this.refreshPromise = (async () => {
      const refreshToken = this.getRefreshToken();
      if (!refreshToken) {
        throw new Error('No refresh token available');
      }

      const response = await fetch(`${this.baseUrl}/api/v1/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken }),
      });

      if (!response.ok) {
        throw new Error('Token refresh failed');
      }

      const data = (await response.json()) as ApiResponse<AuthTokens>;
      this.setTokens(data.data);
      this.emit('token_refreshed', data.data);
      return data.data;
    })();

    try {
      return await this.refreshPromise;
    } finally {
      this.refreshPromise = null;
    }
  }

  /** Logout — clear tokens and notify */
  async logout(): Promise<void> {
    try {
      await this.post('/api/v1/auth/logout');
    } catch {
      // Best effort
    } finally {
      this.clearTokens();
      this.emit('logout');
    }
  }
}

/** Custom error class for API errors */
export class ApiClientError extends Error {
  code: string;
  status: number;
  details?: Record<string, unknown>;

  constructor(
    message: string,
    code: string,
    status: number,
    details?: Record<string, unknown>,
  ) {
    super(message);
    this.name = 'ApiClientError';
    this.code = code;
    this.status = status;
    this.details = details;
  }
}

/** Singleton API client instance */
export const apiClient = new ApiClient();

export default ApiClient;
